import React, { useEffect, useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import { Play, Check, X } from "lucide-react";

const DURATION = 60;

function makeProblem() {
  const ops = ["+", "-", "×"];
  const op = ops[Math.floor(Math.random() * ops.length)];
  let a = Math.floor(Math.random() * 12) + 1;
  let b = Math.floor(Math.random() * 12) + 1;
  let answer;
  if (op === "+") answer = a + b;
  else if (op === "-") { if (b > a) [a, b] = [b, a]; answer = a - b; }
  else answer = a * b;
  const wrongs = new Set();
  while (wrongs.size < 3) {
    const delta = Math.floor(Math.random() * 9) - 4 || 5;
    const w = answer + delta;
    if (w !== answer && w >= 0) wrongs.add(w);
  }
  const options = [...wrongs, answer].sort(() => Math.random() - 0.5);
  return { text: `${a} ${op} ${b}`, answer, options };
}

export function MathBlitz({ onScore }) {
  const [problem, setProblem] = useState(makeProblem);
  const [score, setScore] = useState(0);
  const [time, setTime] = useState(DURATION);
  const [running, setRunning] = useState(false);
  const [over, setOver] = useState(false);
  const [feedback, setFeedback] = useState(null);
  const reported = useRef(false);

  useEffect(() => {
    if (!running) return;
    if (time <= 0) { setRunning(false); setOver(true); return; }
    const id = setTimeout(() => setTime((t) => t - 1), 1000);
    return () => clearTimeout(id);
  }, [running, time]);

  const start = () => {
    setScore(0); setTime(DURATION); setOver(false); setRunning(true);
    setProblem(makeProblem()); reported.current = false;
  };

  const answer = (opt) => {
    if (!running) return;
    if (opt === problem.answer) {
      setScore((s) => s + 1);
      setFeedback("ok");
    } else {
      setScore((s) => Math.max(0, s - 1));
      setFeedback("no");
    }
    setTimeout(() => setFeedback(null), 200);
    setProblem(makeProblem());
  };

  useEffect(() => {
    if (over && !reported.current) { reported.current = true; onScore?.(score * 50); }
  }, [over, score, onScore]);

  return (
    <div className="flex flex-col items-center w-full" data-testid="math-blitz">
      <div className="flex items-center justify-between w-full max-w-md mb-6 font-mono">
        <span className="text-neon-cyan text-lg" data-testid="math-score">SOLVED: {score}</span>
        <span className={`text-lg ${time <= 10 ? "text-neon-pink animate-blink" : "text-neon-yellow"}`}>⏱ {time}s</span>
      </div>

      {!running && (
        <div className="text-center py-10">
          {over && <p className="font-display font-black text-2xl text-neon-lime uppercase mb-2" data-testid="math-result">Time! You solved {score}</p>}
          <Button onClick={start} data-testid="math-start" className="bg-neon-cyan text-black rounded-none font-bold uppercase h-12 px-8">
            <Play className="w-5 h-5 mr-2 fill-black" /> {over ? "Play Again" : "Start 60s Blitz"}
          </Button>
        </div>
      )}

      {running && (
        <div className="w-full max-w-md">
          <div className={`text-center py-8 rounded-xl border-2 mb-6 transition-colors ${
            feedback === "ok" ? "border-neon-lime bg-neon-lime/10" :
            feedback === "no" ? "border-neon-pink bg-neon-pink/10" : "border-zinc-700 bg-zinc-900"}`}>
            <span className="font-display font-black text-5xl">{problem.text}</span>
            <span className="font-display font-black text-5xl text-zinc-600"> = ?</span>
          </div>
          <div className="grid grid-cols-2 gap-3">
            {problem.options.map((opt, i) => (
              <button key={i} onClick={() => answer(opt)} data-testid={`math-option-${i}`}
                className="py-6 rounded-lg bg-zinc-900 border-2 border-zinc-700 hover:border-neon-cyan font-display font-bold text-2xl transition-colors">
                {opt}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
